import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { renderMarkdown } from '../../markdown';

/**
 * <cadence-markdown-renderer .markdown=${src}> — renders a trusted-subset Markdown
 * string to styled HTML. The renderer (src/client/markdown.ts) HTML-escapes input and
 * restricts URLs, so the output is safe to pass through unsafeHTML. Used for event
 * details docs, event notes, and anywhere we show authored Markdown.
 */
@customElement('cadence-markdown-renderer')
export class CadenceMarkdownRenderer extends LitElement {
  @property() markdown = '';

  static styles = css`
    :host { display: block; }
    .md :first-child { margin-top: 0; }
    .md :last-child { margin-bottom: 0; }
    .md h1, .md h2, .md h3 { font-size: 1.1rem; margin: 14px 0 6px; }
    .md p, .md ul, .md ol { margin: 8px 0; }
    .md pre { background: var(--color-bg-secondary); padding: 10px; border-radius: 6px; overflow: auto; }
    .md code { background: var(--color-bg-secondary); padding: 1px 4px; border-radius: 4px; }
    .md pre code { padding: 0; background: none; }
    .md blockquote { margin: 8px 0; padding-left: 12px; border-left: 3px solid var(--color-border); color: var(--color-text-secondary); }
    .md a { color: var(--color-primary); }
    .md table { border-collapse: collapse; width: 100%; margin: 10px 0; font-size: 0.92rem; }
    .md th, .md td { border: 1px solid var(--color-border); padding: 6px 10px; text-align: left; }
    .md th { background: var(--color-bg-secondary); font-weight: 600; }
    .empty { color: var(--color-text-secondary); font-style: italic; }
  `;

  render() {
    const src = typeof this.markdown === 'string' ? this.markdown : String(this.markdown ?? '');
    if (!src.trim()) return html`<p class="empty">Nothing to show.</p>`;
    return html`<div class="md">${unsafeHTML(renderMarkdown(src))}</div>`;
  }
}
